import { Col, Form, Input, Modal, Select, Table, notification } from "antd";
import { useEffect, useState } from "react";
import "../../shared/styles/list.css";
import { columns } from "./columns";
import { getCountries, updateCountry } from "../../services/countryApi";
import { getCurrencies } from "../../services/currencyApi";
import { Country } from "../../shared/dtos/country";
import ContainerLayout from "../common/containerLayout";

const { Search } = Input;

const normalize = (text: string) =>
  text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const CountryList = () => {
  const [data, setData] = useState<Country[]>();
  const [filteredData, setFilteredData] = useState<Country[]>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [currencies, setCurrencies] = useState<{ code: string }[]>([]);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setFetching(true);
    const [countriesData, currenciesData] = await Promise.all([
      getCountries(),
      getCurrencies(),
    ]);
    setData(countriesData);
    setFilteredData(countriesData);
    setCurrencies(currenciesData);
    setFetching(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onSearch = (value: string) => {
    if (!value || !data) {
      setFilteredData(data);
      return;
    }
    const term = normalize(value);
    setFilteredData(
      data.filter(
        (c) =>
          normalize(c.name).includes(term) ||
          normalize(c.iso ?? '').includes(term) ||
          normalize(c.currency ?? '').includes(term),
      ),
    );
  };

  const handleEdit = (record: Country) => {
    setEditingCountry(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (editingCountry) {
        await updateCountry(editingCountry.id, { currency: values.currency });
        notification.success({ message: "País actualizado" });
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      if (error instanceof Error) {
        notification.error({ message: error.message });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContainerLayout title="Países" showNotification={false}>
      <Col span={24}>
        <Search
          placeholder="Buscar por nombre, ISO o moneda"
          allowClear
          onSearch={onSearch}
          onChange={(e) => { if (!e.target.value) onSearch(''); }}
          loading={fetching}
          disabled={fetching}
          style={{ marginBottom: 16, maxWidth: 400 }}
        />
        <Table
          style={{ minWidth: "100%" }}
          columns={columns({ handleEdit })}
          rowKey={(record) => record.id}
          dataSource={filteredData}
          scroll={{ y: "calc(100vh - 420px)" }}
        />
      </Col>
      <Modal
        title="Editar país"
        open={modalOpen}
        onOk={handleOk}
        onCancel={() => setModalOpen(false)}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Nombre">
            <Input disabled />
          </Form.Item>
          <Form.Item name="iso" label="ISO">
            <Input disabled />
          </Form.Item>
          <Form.Item name="currency" label="Moneda" rules={[{ required: true, message: "Seleccione la moneda" }]}>
            <Select
              showSearch
              placeholder="Seleccione una moneda"
              filterOption={(input, option) =>
                (option?.label as string ?? '')
                  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                  .toLowerCase()
                  .includes(
                    input.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
                  )
              }
              options={currencies.map((c) => ({
                value: c.code,
                label: `${c.code}`,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </ContainerLayout>
  );
};

export default CountryList;
